import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Table } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { useSort } from '~/hooks/useSort';
import type { LabelGroupListItem } from '~/modules/settings/api/lib/schemas';
import { SETTINGS_ROUTES } from '~/modules/settings/config/routes';
import { sortByPriority } from '~/modules/settings/utils/roles';
import { useLabelGroupList } from '~settings/api/queries/labelGroupList';
import { HeaderSmall } from '~settings/components/HeaderSmall';
import { ListControls } from '~settings/components/ListControls';
import { TableCellSkeleton } from '~settings/components/TableCellSkeleton';
import { useFilters } from '~settings/hooks/useFilters';
import { useUserDetail } from '~settings/modules/user/pages/DetailUserPage/hooks/useUserDetail';
import { useUserLabelGroupsColumns } from '~settings/modules/user/pages/DetailUserPage/hooks/useUserLabelGroupsColumns';

export function UserLabelGroups() {
  const columns = useUserLabelGroupsColumns();
  const { t } = useTranslation('settings');
  const { id, rolesByResource } = useUserDetail();
  const { page, setPageNumber, perPage, skip, take } = usePagination();
  const { sorting, setSorting, sortParams } = useSort();
  const { filterData } = useFilters();

  const { labelGroupList, isLoading } = useLabelGroupList({
    skip,
    take,
    userId: id,
    withSpecialGroups: true,
    ...sortParams,
    ...filterData,
  });

  const labelGroups = useMemo(() => {
    if (!labelGroupList?.data) {
      return [];
    }

    return labelGroupList.data.map((labelGroup) => ({
      ...labelGroup,
      roles: sortByPriority(
        rolesByResource.get(`${labelGroup.id}-RESOURCE_GROUP`) ?? [],
      ),
    }));
  }, [labelGroupList?.data, rolesByResource]);

  const navigate = useNavigate();

  const onClick = (row: LabelGroupListItem) => {
    navigate(SETTINGS_ROUTES.LABEL_GROUP_DETAIL(row.id));
  };

  return (
    <>
      <HeaderSmall className="mb-0">{t('labelGroups')}</HeaderSmall>
      <ListControls
        placeholder={t('labelGroup.search')}
        page={page}
        total={labelGroupList?.total ?? 0}
        perPage={perPage}
        entity="labelGroups"
        setPageNumber={setPageNumber}
      />
      <div className="pt-lg">
        <Table
          CellSkeleton={TableCellSkeleton}
          columns={columns}
          data={labelGroups}
          isLoading={isLoading}
          loadingRowCount={perPage}
          onRowClick={onClick}
          onSortingChange={setSorting}
          sort={sorting}
          size="md"
          variant="striped"
          level={3}
        />
      </div>
    </>
  );
}
UserLabelGroups.displayName = 'UserLabelGroups';
